import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { round } from 'lodash'
import ReactECharts from 'echarts-for-react'
import { Dialog, DialogContent, Grid, Card, Box, Typography, Tooltip, ToggleButton, ToggleButtonGroup } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { aquaFetch } from 'common/utils/ActionUtils'
import { formatMilliers } from 'common/utils/StringUtil'
import ApplicationConf from 'conf/ApplicationConf'
import { STATUS } from 'common/constants/storeConstants'
import { BasicDialogTitle } from 'common/components/modal/DialogComponents'
import { MONTH, computeSynthese } from '../StatisticsUtil'

const PASTEL_RED = '#ef9a9a'
const PASTEL_GREEN = '#a5d6a7'

// KPI affichés : mêmes 5 indicateurs que les cartes de synthèse de LotsReportCard.
// `description` reprend le tooltip du tableau quand la colonne existe (retard / tauxRetard),
// sinon décrit la logique métier de l'indicateur.
// `threshold` + `goodWhen` : seuil affiché en markLine et coloration par mois (vert = bon, rouge = mauvais).
const LOT_KPIS = [
    { key: 'nbLots', label: 'Lots', unit: '', description: 'Nombre de lots dans le périmètre sélectionné.' },
    { key: 'nbTermines', label: 'Lots terminés', unit: '', description: 'Lots dont tous les tickets sont terminés (dernier ticket passé en « Attente de validation », statut ≥ 60).' },
    { key: 'tauxRespect', label: '% respectés', unit: '%', description: 'Part des lots terminés et dotés d\'une échéance dont l\'échéance a été respectée.', threshold: 80, goodWhen: 'above' },
    { key: 'retardMoyen', label: 'Retard moyen (jours)', unit: 'j', description: 'Retard moyen calculé ticket par ticket puis moyenné.\nPour chaque ticket : écart entre sa date de fin (la plus proche entre la fin du ticket et la clôture du lot, ou aujourd\'hui s\'il est encore ouvert) et son échéance.\nUn ticket sans échéance prend l\'échéance du lot.', threshold: 5, goodWhen: 'below' },
    { key: 'tauxRetardMoyen', label: 'Taux de retard moyen', unit: '%', description: 'Retard du lot rapporté à sa durée (retard / durée). Vide pour les lots en cours.' },
]

// Données de barres : quand un seuil est défini, chaque mois est coloré (vert = bon côté du seuil, rouge = mauvais).
const buildBarData = (kpi, values) => {
    if (!Number.isFinite(kpi.threshold)) {
        return values
    }
    return values.map(v => {
        if (!Number.isFinite(v)) {
            return v
        }
        const isGood = kpi.goodWhen === 'above' ? v >= kpi.threshold : v <= kpi.threshold
        return { value: v, itemStyle: { color: isGood ? PASTEL_GREEN : PASTEL_RED } }
    })
}

// Découpe la plage [startDate, endDate] (secondes) en intervalles mensuels { start, end }, bornés à
// la plage sélectionnée. endOf('month') gère automatiquement le nombre de jours (28/29 pour février).
// - Cumulé (par défaut) : chaque mois garde le début de la plage, seule la fin glisse (fait par l'appelant).
// - Non cumulé : chaque appel couvre uniquement son mois (01/02 -> 28/02).
const buildMonthlyRanges = (startSec, endSec) => {
    const start = moment.unix(startSec)
    const end = moment.unix(endSec)
    const monthsCount = end.clone().startOf('month').diff(start.clone().startOf('month'), 'months') + 1
    return Array.from({ length: monthsCount }, (_, i) => ({
        start: moment.max(start.clone().add(i, 'month').startOf('month'), start),
        end: moment.min(start.clone().add(i, 'month').endOf('month'), end),
    }))
}

const rangeLabel = (range) => `${MONTH[range.start.month() + 1]} ${range.start.format('YY')}`

// Enchaîne des tâches (fonctions renvoyant une Promise) les unes après les autres. Chaque résultat
// est remonté via onResult au fil de l'eau (remplissage progressif des graphs). S'arrête dès que le
// signal est aborté (fermeture du dialog). Style récursif (pas de boucle mutable, cf. conventions).
const runSequentially = (tasks, index, onResult, signal) => {
    if (index >= tasks.length || signal.aborted) {
        return Promise.resolve()
    }
    return tasks[index]().then(res => {
        if (signal.aborted) {
            return undefined
        }
        onResult(res)
        return runSequentially(tasks, index + 1, onResult, signal)
    })
}

const KpiChart = ({ kpi, labels, values, loading }) => {
    const unitSuffix = kpi.unit ? ` ${kpi.unit}` : ''
    const fmt = (v) => `${formatMilliers(v, true)}${unitSuffix}`
    // Delta signé vs mois précédent, arrondi à 1 décimale (la soustraction de 2 flottants
    // introduit sinon du bruit type -4.80000000001). Le '-' est déjà porté par les valeurs négatives.
    // L'écart entre 2 pourcentages est en points : pas d'unité affichée.
    const deltaSuffix = kpi.unit === '%' ? '' : unitSuffix
    const fmtDelta = (v) => {
        const r = round(v, 1)
        return `${r > 0 ? '+' : ''}${formatMilliers(r, true)}${deltaSuffix}`
    }

    // Label de colonne : valeur du mois + évolution vs mois précédent (2e ligne, en plus discret).
    const barLabel = (params) => {
        const cur = values[params.dataIndex]
        if (!Number.isFinite(cur)) {
            return ''
        }
        const prev = values[params.dataIndex - 1]
        if (!Number.isFinite(prev)) {
            return `{main|${fmt(cur)}}`
        }
        return `{main|${fmt(cur)}}\n{delta|${fmtDelta(cur - prev)}}`
    }

    const options = {
        // Titre porté par ECharts (et non par un header React) pour qu'il soit inclus dans l'image
        // lors d'un « copier/enregistrer l'image » (clic droit) sur le canvas.
        title: { text: kpi.label, left: 'center', top: 8 },
        grid: { top: 58, bottom: 40, containLabel: true },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params) => {
                const header = params[0]?.axisValue ?? ''
                const value = params[0]?.value
                return `${header}<br/>${Number.isFinite(value) ? fmt(value) : '-'}`
            },
        },
        xAxis: {
            type: 'category',
            data: labels,
            axisLabel: { interval: 0, rotate: 40 },
        },
        yAxis: {
            type: 'value',
            // Max forcé pour laisser voir le seuil (ex. 80 % alors que les valeurs plafonnent à 40)
            // et garder de la marge au-dessus des labels sur 2 lignes.
            max: ({ max }) => {
                const base = Number.isFinite(max) ? max : 0
                const top = Number.isFinite(kpi.threshold) ? Math.max(base, kpi.threshold) : base
                return top > 0 ? Math.ceil(top * 1.15) : undefined
            },
            axisLabel: { formatter: (v) => formatMilliers(v) },
        },
        series: [{
            name: kpi.label,
            type: 'bar',
            data: buildBarData(kpi, values),
            label: {
                show: true,
                position: 'top',
                formatter: barLabel,
                rich: {
                    main: { fontWeight: 'bold', fontSize: 12, lineHeight: 15 },
                    delta: { fontSize: 10, color: '#757575', lineHeight: 13 },
                },
            },
            markLine: Number.isFinite(kpi.threshold) ? {
                symbol: 'none',
                data: [{ yAxis: kpi.threshold }],
                lineStyle: { type: 'dashed', color: '#616161' },
                label: { formatter: `Objectif ${formatMilliers(kpi.threshold, true)}${unitSuffix}`, position: 'insideEndTop' },
            } : undefined,
        }],
    }

    return (
        <Card sx={{ position: 'relative' }}>
            <Box sx={{ position: 'absolute', top: 6, right: 6, zIndex: 1 }}>
                <Tooltip
                    title={<span style={{ whiteSpace: 'pre-line' }}>{kpi.description}</span>}
                    slotProps={{ popper: { sx: { zIndex: theme => `${theme.zIndex.modal + 1} !important` } } }}
                >
                    <InfoOutlinedIcon fontSize='small' sx={{ color: 'action.active', cursor: 'help' }} />
                </Tooltip>
            </Box>
            <ReactECharts
                option={options}
                notMerge={true}
                lazyUpdate={true}
                showLoading={loading}
                style={{ height: '300px' }}
            />
        </Card>
    )
}

KpiChart.propTypes = {
    kpi: PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string,
        unit: PropTypes.string,
        description: PropTypes.string,
        threshold: PropTypes.number,
        goodWhen: PropTypes.oneOf(['above', 'below']),
    }),
    labels: PropTypes.arrayOf(PropTypes.string),
    values: PropTypes.arrayOf(PropTypes.number),
    loading: PropTypes.bool,
}

const LotsEvolutionDialog = ({
    open,
    onClose,
    filter,
    includeIaeau,
    excludedLots,
}) => {
    const [status, setStatus] = useState(STATUS.IDLE)
    const [labels, setLabels] = useState([])
    // Synthèses mensuelles remplies au fur et à mesure des appels séquentiels.
    const [syntheses, setSyntheses] = useState([])
    // Cumulé : chaque mois part du début de la plage (KPI glissant). Non cumulé : chaque mois isolé.
    const [cumulative, setCumulative] = useState(true)

    useEffect(() => {
        if (!open) {
            return undefined
        }
        const controller = new AbortController()
        const { signal } = controller
        const ranges = buildMonthlyRanges(filter.startDate, filter.endDate)
        setLabels(ranges.map(rangeLabel))
        setSyntheses([])
        setStatus(STATUS.LOADING)

        // Un appel par mois, même périmètre que la card courante (projets, catégories, toggle IAEAU) ;
        // les lots exclus manuellement sont retirés avant recalcul pour rester cohérent avec les cartes.
        // Cumulé : startDate figé au début de la plage. Non cumulé : startDate = début du mois courant.
        const tasks = ranges.map(range => () =>
            aquaFetch(ApplicationConf.support.statisticsLots(), {
                method: 'POST',
                body: {
                    ...filter,
                    startDate: cumulative ? filter.startDate : range.start.unix(),
                    endDate: range.end.unix(),
                    includeIaeau,
                },
                signal,
            }).then(data => computeSynthese((data?.lots ?? []).filter(l => !excludedLots.has(l.lot)))),
        )

        runSequentially(tasks, 0, synthese => setSyntheses(prev => [...prev, synthese]), signal)
            .then(() => {
                if (!signal.aborted) {
                    setStatus(STATUS.SUCCEEDED)
                }
            })
            .catch(() => {
                if (!signal.aborted) {
                    setStatus(STATUS.REJECTED)
                }
            })

        return () => controller.abort()
    }, [open, filter, includeIaeau, excludedLots, cumulative])

    const loading = status === STATUS.LOADING

    return (
        <Dialog open={open} onClose={onClose} maxWidth='xl' fullWidth>
            <BasicDialogTitle onClose={onClose}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <span>{'Évolution mensuelle des lots'}</span>
                    <ToggleButtonGroup
                        value={cumulative ? 'cumulative' : 'monthly'}
                        exclusive
                        size='small'
                        onChange={(_, value) => value !== null && setCumulative(value === 'cumulative')}
                        sx={{
                            backgroundColor: 'white',
                            borderRadius: 1,
                            marginRight: 3,
                            '& .MuiToggleButtonGroup-grouped': {
                                border: 0,
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                paddingY: 0.25,
                                paddingX: 1.5,
                                color: 'text.primary',
                            },
                        }}
                    >
                        <ToggleButton value='cumulative'>{'Cumulé'}</ToggleButton>
                        <ToggleButton value='monthly'>{'Non cumulé'}</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </BasicDialogTitle>
            <DialogContent>
                {status === STATUS.REJECTED && syntheses.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                        <Typography color='error'>{'Erreur lors du chargement de l\'évolution.'}</Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2} alignItems='flex-start' sx={{ paddingTop: 1 }}>
                        {LOT_KPIS.map((kpi, index) => (
                            <Grid key={kpi.key} size={index < 3 ? 4 : 6}>
                                <KpiChart
                                    kpi={kpi}
                                    labels={labels}
                                    values={syntheses.map(s => s[kpi.key])}
                                    loading={loading}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </DialogContent>
        </Dialog>
    )
}

LotsEvolutionDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    filter: PropTypes.shape({
        startDate: PropTypes.number,
        endDate: PropTypes.number,
        projects: PropTypes.arrayOf(PropTypes.string),
        categories: PropTypes.arrayOf(PropTypes.string),
    }),
    includeIaeau: PropTypes.bool,
    excludedLots: PropTypes.instanceOf(Set),
}

export default LotsEvolutionDialog
